import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { usePostAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import { OfficeCommentWithFlagDtoIn } from '@/models/common/OfficeCommentWithFlagDtoIn';
import { formatDateTimeLocale } from '@/helpers/dateHelper';
import deleteIcon from '@/assets/icons/delete.svg';
import styles from './OfficeCommentsDialog.module.css';

interface AppendOfficeCommentDtoOut {
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
  comment: string;
  showInPersonalArea: boolean;
}

interface SendOfficeCommentDtoOut {
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
  commentIndex: number;
  isVerified: boolean;
}

interface SendOfficeCommentDtoIn {
  requestorEmail: string;
  emailSubject: string;
  emailHtmlContent: string;
}

interface UpdateOfficeCommentVisibilityDtoOut {
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
  commentIndex: number;
  showInPersonalArea: boolean;
}

interface DeleteOfficeCommentDtoOut {
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
  commentIndex: number;
  // The server deletes only if this is still the text at commentIndex.
  expectedComment: string;
}

interface OfficeCommentsDialogProps {
  open: boolean;
  onClose: () => void;
  correlationId: string;
  counter: number | null;
  totalCount: number;
  isInInitialStatusProcess: boolean;
  officeComments: OfficeCommentWithFlagDtoIn[];
  onSuccess: () => void;
  supportedName?: string;
}

const OfficeCommentsDialog = ({
  open,
  onClose,
  correlationId,
  counter,
  totalCount,
  isInInitialStatusProcess,
  officeComments,
  onSuccess,
  supportedName,
}: OfficeCommentsDialogProps) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendingIndex, setSendingIndex] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<SendOfficeCommentDtoIn | null>(null);
  const [pendingSendIndex, setPendingSendIndex] = useState<number | null>(null);
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  // The comment as it was when the office clicked delete - the list may refetch underneath the confirmation.
  const [pendingDelete, setPendingDelete] = useState<{ index: number; comment: OfficeCommentWithFlagDtoIn } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: appendComment, isPending: isAppending } = usePostAuthApi<void, AppendOfficeCommentDtoOut>(
    'management/support-requests/office-comment'
  );

  const { mutate: sendComment, isPending: isSending } = usePutAuthApi<SendOfficeCommentDtoIn, object, SendOfficeCommentDtoOut>(
    'management/support-requests/send-office-comment'
  );

  const { mutate: updateVisibility, isPending: isUpdatingVisibility } = usePutAuthApi<void, object, UpdateOfficeCommentVisibilityDtoOut>(
    'management/support-requests/office-comment-visibility'
  );

  const { mutate: deleteComment, isPending: isDeleting } = usePostAuthApi<void, DeleteOfficeCommentDtoOut>(
    'management/support-requests/office-comment/delete'
  );

  const isBusy = isAppending || isSending || isUpdatingVisibility || isDeleting;

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;

    setDeleteError(null);
    setSuccessMessage(null);

    const body: DeleteOfficeCommentDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : counter,
      isInInitialStatusProcess,
      commentIndex: pendingDelete.index,
      expectedComment: pendingDelete.comment.comment,
    };

    deleteComment(body, {
      onSuccess: () => {
        setPendingDelete(null);
        setSuccessMessage(t('officeComments.commentDeleted'));
        queryClient.refetchQueries({
          predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
        });
        onSuccess();
      },
      onError: (err: unknown) => {
        console.error('Error deleting office comment:', err);
        const axiosError = err as { response?: { status?: number } };
        // 409: the list changed since it was loaded, so the index no longer points at this comment.
        // Close the confirmation and reload the list - the office picks again from what is there now.
        if (axiosError.response?.status === 409) {
          setPendingDelete(null);
          setError(t('officeComments.commentsChanged'));
          queryClient.refetchQueries({
            predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
          });
          onSuccess();
        } else {
          setDeleteError(t('officeComments.errorDeletingComment'));
        }
      },
    });
  };

  const handleAddComment = (andSend: boolean) => {
    if (!newComment.trim()) {
      setError(t('officeComments.commentRequired'));
      return;
    }

    setError(null);
    setSuccessMessage(null);

    const body: AppendOfficeCommentDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : counter,
      isInInitialStatusProcess,
      comment: newComment.trim(),
      showInPersonalArea,
    };

    appendComment(
      body,
      {
        onSuccess: () => {
          setNewComment('');
          setShowInPersonalArea(true);
          setSuccessMessage(t('officeComments.commentAdded'));
          queryClient.refetchQueries({
            predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
          });
          onSuccess();

          if (andSend) {
            // The new comment will be at the end of the list
            const newIndex = officeComments.length;
            handleSendComment(newIndex);
          }
        },
        onError: (err: unknown) => {
          console.error('Error adding office comment:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorAddingComment'));
        },
      }
    );
  };

  const handleSendComment = (index: number) => {
    setError(null);
    setSuccessMessage(null);
    setSendingIndex(index);

    const body: SendOfficeCommentDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : counter,
      isInInitialStatusProcess,
      commentIndex: index,
      isVerified: false, // First call is just for preview
    };

    sendComment(
      { body },
      {
        onSuccess: (data) => {
          setSendingIndex(null);
          setPreviewData(data);
          setPendingSendIndex(index);
        },
        onError: (err: unknown) => {
          console.error('Error getting email preview:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorSendingComment'));
          setSendingIndex(null);
        },
      }
    );
  };

  const handleConfirmSend = () => {
    if (pendingSendIndex === null) return;

    setError(null);
    setSendingIndex(pendingSendIndex);

    const body: SendOfficeCommentDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : counter,
      isInInitialStatusProcess,
      commentIndex: pendingSendIndex,
      isVerified: true, // Actual send
    };

    sendComment(
      { body },
      {
        onSuccess: () => {
          setSendingIndex(null);
          setPreviewData(null);
          setPendingSendIndex(null);
          setSuccessMessage(t('officeComments.commentSent'));
          queryClient.refetchQueries({
            predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
          });
          onSuccess();
        },
        onError: (err: unknown) => {
          console.error('Error sending office comment:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorSendingComment'));
          setSendingIndex(null);
          setPreviewData(null);
          setPendingSendIndex(null);
        },
      }
    );
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    setPendingSendIndex(null);
  };

  const handleToggleVisibility = (index: number, currentValue: boolean) => {
    setError(null);
    setSuccessMessage(null);

    const body: UpdateOfficeCommentVisibilityDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : counter,
      isInInitialStatusProcess,
      commentIndex: index,
      showInPersonalArea: !currentValue,
    };

    updateVisibility(
      { body },
      {
        onSuccess: () => {
          setSuccessMessage(t('officeComments.visibilityUpdated'));
          queryClient.refetchQueries({
            predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
          });
          onSuccess();
        },
        onError: (err: unknown) => {
          console.error('Error toggling visibility:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorTogglingVisibility'));
        },
      }
    );
  };

  const handleClose = () => {
    setNewComment('');
    setShowInPersonalArea(true);
    setError(null);
    setSuccessMessage(null);
    setPreviewData(null);
    setPendingSendIndex(null);
    setPendingDelete(null);
    setDeleteError(null);
    onClose();
  };

  // Preview dialog
  if (previewData) {
    return (
      <MainDialogLayout
        open={open}
        onOpenChange={(isOpen: boolean) => !isOpen && handleCancelPreview()}
        title={t('officeComments.confirmSendTitle')}
      >
        <div className={styles.previewContent}>
          <p className="body-md-regular text-(--font-body-1)">
            {previewData.requestorEmail ? (
              <Trans
                i18nKey="officeComments.confirmSendMessageTo"
                values={{ email: previewData.requestorEmail }}
                components={{ email: <bdi dir="ltr" className="body-md-semibold" /> }}
              />
            ) : (
              t('officeComments.confirmSendMessage')
            )}
          </p>

          <div className={styles.emailPreview}>
            <div className={styles.emailSubject}>
              <strong>{t('officeComments.subject')}:</strong> {previewData.emailSubject}
            </div>
            <div className={styles.emailBody} dangerouslySetInnerHTML={{ __html: previewData.emailHtmlContent }} />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button className={styles.secondaryButton} onClick={handleCancelPreview} disabled={isSending}>
              {t('cancel')}
            </button>
            <button className={styles.primaryButton} onClick={handleConfirmSend} disabled={isSending}>
              {isSending ? t('loading') : t('officeComments.sendEmail')}
            </button>
          </div>
        </div>
      </MainDialogLayout>
    );
  }

  // Split comments into two groups - keep track of original index for sending
  const initialStatusComments = officeComments
    .map((comment, index) => ({ comment, originalIndex: index }))
    .filter(({ comment }) => comment.isCommentOfInitialStatus);
  const requestStatusComments = officeComments
    .map((comment, index) => ({ comment, originalIndex: index }))
    .filter(({ comment }) => !comment.isCommentOfInitialStatus);

  const renderCommentsTable = (comments: { comment: OfficeCommentWithFlagDtoIn; originalIndex: number }[]) => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>{t('officeComments.comment')}</th>
          <th>{t('officeComments.date')}</th>
          <th>{t('officeComments.createdBy')}</th>
          <th>{t('officeComments.visibility')}</th>
          <th>{t('officeComments.sendStatus')}</th>
          <th aria-label={t('officeComments.deleteComment')} />
        </tr>
      </thead>
      <tbody>
        {comments.map(({ comment, originalIndex }) => (
          <tr key={originalIndex}>
            <td className={styles.noteCell} title={comment.comment}>
              {comment.comment}
            </td>
            <td className={styles.dateCell}>{formatDateTimeLocale(comment.date)}</td>
            <td>{comment.updatedBy || '-'}</td>
            <td className={styles.visibilityCell}>
              <button
                className={comment.showInPersonalArea ? styles.visibleButton : styles.hiddenButton}
                onClick={() => handleToggleVisibility(originalIndex, comment.showInPersonalArea)}
                disabled={isUpdatingVisibility}
                title={comment.showInPersonalArea ? t('officeComments.visibleInPersonalArea') : t('officeComments.showInPersonalArea')}
              >
                {comment.showInPersonalArea ? t('officeComments.hideFromUser') : t('officeComments.showToUser')}
              </button>
            </td>
            <td className={styles.statusCell}>
              {comment.wasSent ? (
                <div className={styles.sentInfo}>
                  <span>
                    {t('officeComments.sentOn')}: {comment.sendDate ? formatDateTimeLocale(comment.sendDate) : '-'}
                  </span>
                  <span>
                    {t('officeComments.sentBy')}: {comment.sentBy || '-'}
                  </span>
                </div>
              ) : (
                <button
                  className={styles.sendButton}
                  onClick={() => handleSendComment(originalIndex)}
                  disabled={sendingIndex === originalIndex || isAppending}
                >
                  {sendingIndex === originalIndex ? t('loading') : t('officeComments.sendToRequestor')}
                </button>
              )}
            </td>
            <td className={styles.deleteCell}>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => {
                  setDeleteError(null);
                  setPendingDelete({ index: originalIndex, comment });
                }}
                disabled={isBusy}
                title={t('officeComments.deleteComment')}
                aria-label={t('officeComments.deleteComment')}
              >
                <img src={deleteIcon} alt="" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Main dialog
  const dialogTitle = supportedName
    ? t('officeComments.titleWithSupportedName', { name: supportedName })
    : t('officeComments.title');

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && handleClose()} title={dialogTitle}>
      <div className={styles.content}>
        {/* Initial Status Notes */}
        <div className={styles.tableSection}>
          <h4 className="body-md-semibold text-(--font-heading)">{t('officeComments.initialStatusComments')}</h4>
          {initialStatusComments.length > 0 ? (
            renderCommentsTable(initialStatusComments)
          ) : (
            <div className={styles.noComments}>{t('officeComments.noInitialStatusComments')}</div>
          )}
        </div>

        {/* Request Status Notes - only show if not in initial status process */}
        {!isInInitialStatusProcess && (
          <div className={styles.tableSection}>
            <h4 className="body-md-semibold text-(--font-heading)">
              {t('officeComments.requestStatusComments', { counter, totalCount })}
            </h4>
            {requestStatusComments.length > 0 ? (
              renderCommentsTable(requestStatusComments)
            ) : (
              <div className={styles.noComments}>{t('officeComments.noRequestStatusComments')}</div>
            )}
          </div>
        )}

        {/* Add new note section */}
        <div className={styles.addNoteSection}>
          <h4 className="body-md-semibold text-(--font-heading)">{t('officeComments.addComment')}</h4>
          <ToggleSwitch
            label={t('officeComments.showInPersonalArea')}
            checked={showInPersonalArea}
            onChange={setShowInPersonalArea}
          />
          <textarea
            className={styles.textarea}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('officeComments.commentPlaceholder')}
            rows={3}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

        <div className={styles.actions}>
          <button className={styles.secondaryButton} onClick={handleClose} disabled={isBusy}>
            {t('close')}
          </button>
          <button
            className={styles.tertiaryButton}
            onClick={() => handleAddComment(true)}
            disabled={isBusy || !newComment.trim()}
          >
            {isAppending ? t('loading') : t('officeComments.saveAndSend')}
          </button>
          <button
            className={styles.primaryButton}
            onClick={() => handleAddComment(false)}
            disabled={isBusy || !newComment.trim()}
          >
            {isAppending ? t('loading') : t('save')}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isDeleting) setPendingDelete(null);
        }}
        title={t('officeComments.confirmDeleteTitle')}
        message={pendingDelete?.comment.comment}
        confirmText="officeComments.deleteComment"
        cancelText="cancel"
        variant="danger"
        isLoading={isDeleting}
        error={deleteError}
        onConfirm={handleConfirmDelete}
      >
        {pendingDelete?.comment.wasSent && (
          <p className="body-sm-regular text-(--font-body-2)">{t('officeComments.deleteSentNote')}</p>
        )}
      </ConfirmationDialog>
    </MainDialogLayout>
  );
};

export default OfficeCommentsDialog;
